import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { workTypeSchema, WorkTypeFormData } from "@/schemas/workTypeSchemas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

interface WorkTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workType?: any;
}

export function WorkTypeFormDialog({
  open,
  onOpenChange,
  workType,
}: WorkTypeFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<WorkTypeFormData>({
    resolver: zodResolver(workTypeSchema),
    defaultValues: {
      name: workType?.name || "",
      mold_capture: workType?.mold_capture?.toString() || "0",
      element_type: workType?.element_type || "Pieza",
      requires_machine: workType?.requires_machine || false,
      is_active: workType?.is_active !== undefined ? workType.is_active : true,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: WorkTypeFormData) => {
      const payload = {
        name: data.name,
        mold_capture: parseInt(data.mold_capture),
        element_type: data.element_type,
        requires_machine: data.requires_machine,
        is_active: data.is_active,
      };

      if (workType) {
        const { error } = await api
          .from("work_types")
          .update(payload)
          .eq("id", workType.id);
        if (error) throw error;
      } else {
        const { error } = await api.from("work_types").insert([payload]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["work_types"] });
      toast({
        title: "Éxito",
        description: `Tipo de trabajo ${workType ? "actualizado" : "creado"} correctamente`,
      });
      onOpenChange(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  // Reset form when workType changes or dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        name: workType?.name || "",
      mold_capture: workType?.mold_capture?.toString() || "0",
      element_type: workType?.element_type || "Pieza",
      requires_machine: workType?.requires_machine || false,
      is_active: workType?.is_active !== undefined ? workType.is_active : true,
      });
    }
  }, [open, workType, form]);

const onSubmit = (data: WorkTypeFormData) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {workType ? "Editar Tipo de Trabajo" : "Nuevo Tipo de Trabajo"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mold_capture"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Capturar Molde/Utillaje</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="0">No capturar</SelectItem>
                      <SelectItem value="1">Capturar, no obligatorio</SelectItem>
                      <SelectItem value="2">Capturar, obligatorio</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="element_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Elemento</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Pieza">Pieza</SelectItem>
                      <SelectItem value="Lote">Lote</SelectItem>
                      <SelectItem value="Maquina">Máquina</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="requires_machine"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Requiere máquina</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Activo</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
